import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import blogPosts from "../../../generated/blog-posts";
import siteContent from "../../../generated/site-content";
import { parseAsciiDoc } from "../../../lib/blog/parseAsciiDoc";

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

const copyCodeText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
};

function BlogPage() {
  const { slug } = useParams();
  const articleRef = useRef(null);
  const mobileTocCloseTimerRef = useRef(null);
  const mobileTocAnimationFrameRef = useRef(null);
  const activeHashRef = useRef("");
  const [activeHeading, setActiveHeading] = useState("");
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [shouldRenderMobileToc, setShouldRenderMobileToc] = useState(false);
  const [isMobileTocVisible, setIsMobileTocVisible] = useState(false);
  const currentPost = useMemo(
    () => blogPosts.find((entry) => entry.slug === slug),
    [slug],
  );
  const parsedArticle = useMemo(() => {
    if (!currentPost) {
      return null;
    }

    return parseAsciiDoc(currentPost.content);
  }, [currentPost]);
  const articleHeadings = useMemo(() => parsedArticle?.headings || [], [parsedArticle]);

  useEffect(() => {
    setActiveHeading(articleHeadings[0]?.id || "");
    setIsMobileTocOpen(false);
    setShouldRenderMobileToc(false);
    setIsMobileTocVisible(false);

    if (mobileTocCloseTimerRef.current) {
      window.clearTimeout(mobileTocCloseTimerRef.current);
      mobileTocCloseTimerRef.current = null;
    }

    if (mobileTocAnimationFrameRef.current) {
      window.cancelAnimationFrame(mobileTocAnimationFrameRef.current);
      mobileTocAnimationFrameRef.current = null;
    }
  }, [articleHeadings]);

  useEffect(() => {
    if (mobileTocCloseTimerRef.current) {
      window.clearTimeout(mobileTocCloseTimerRef.current);
      mobileTocCloseTimerRef.current = null;
    }

    if (mobileTocAnimationFrameRef.current) {
      window.cancelAnimationFrame(mobileTocAnimationFrameRef.current);
      mobileTocAnimationFrameRef.current = null;
    }

    if (isMobileTocOpen) {
      if (!shouldRenderMobileToc) {
        setShouldRenderMobileToc(true);
      }

      mobileTocAnimationFrameRef.current = window.requestAnimationFrame(() => {
        setIsMobileTocVisible(true);
        mobileTocAnimationFrameRef.current = null;
      });

      return undefined;
    }

    setIsMobileTocVisible(false);

    if (!shouldRenderMobileToc) {
      return undefined;
    }

    mobileTocCloseTimerRef.current = window.setTimeout(() => {
      setShouldRenderMobileToc(false);
      mobileTocCloseTimerRef.current = null;
    }, 260);

    return () => {
      if (mobileTocCloseTimerRef.current) {
        window.clearTimeout(mobileTocCloseTimerRef.current);
        mobileTocCloseTimerRef.current = null;
      }
    };
  }, [isMobileTocOpen, shouldRenderMobileToc]);

  useEffect(
    () => () => {
      if (mobileTocCloseTimerRef.current) {
        window.clearTimeout(mobileTocCloseTimerRef.current);
      }

      if (mobileTocAnimationFrameRef.current) {
        window.cancelAnimationFrame(mobileTocAnimationFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!slug || !activeHeading) {
      return;
    }

    const nextHash = `#${activeHeading}`;

    if (activeHashRef.current === nextHash) {
      return;
    }

    activeHashRef.current = nextHash;
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}${nextHash}`,
    );
  }, [activeHeading, slug]);

  useEffect(() => {
    if (!shouldRenderMobileToc) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileTocOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [shouldRenderMobileToc]);

  useEffect(() => {
    const articleElement = articleRef.current;

    if (!articleElement) {
      return undefined;
    }

    const resetTimers = new WeakMap();

    const setButtonLabel = (button, label) => {
      button.textContent = label;
    };

    const handleClick = async (event) => {
      const button = event.target.closest(".blog-code-copy");

      if (!button || !articleElement.contains(button)) {
        return;
      }

      const codeElement = button.closest(".blog-code-block")?.querySelector("code");

      if (!codeElement) {
        return;
      }

      const timer = resetTimers.get(button);

      if (timer) {
        window.clearTimeout(timer);
      }

      try {
        const copied = await copyCodeText(codeElement.textContent || "");
        setButtonLabel(button, copied ? "Copied" : "Copy failed");
      } catch {
        setButtonLabel(button, "Copy failed");
      }

      const nextTimer = window.setTimeout(() => {
        setButtonLabel(button, "Copy");
        resetTimers.delete(button);
      }, 1600);

      resetTimers.set(button, nextTimer);
    };

    articleElement.addEventListener("click", handleClick);

    return () => {
      articleElement.removeEventListener("click", handleClick);

      for (const button of articleElement.querySelectorAll(".blog-code-copy")) {
        const timer = resetTimers.get(button);

        if (timer) {
          window.clearTimeout(timer);
        }
      }
    };
  }, [parsedArticle]);

  useEffect(() => {
    if (!articleHeadings.length) {
      return undefined;
    }

    const headingElements = articleHeadings
      .map((heading) => document.getElementById(heading.id))
      .filter(Boolean);

    if (!headingElements.length) {
      return undefined;
    }

    const updateActiveHeading = () => {
      let nextActiveHeading = headingElements[0].id;

      for (const headingElement of headingElements) {
        if (headingElement.getBoundingClientRect().top <= 160) {
          nextActiveHeading = headingElement.id;
          continue;
        }

        break;
      }

      setActiveHeading(nextActiveHeading);
    };

    updateActiveHeading();
    window.addEventListener("scroll", updateActiveHeading, { passive: true });
    window.addEventListener("resize", updateActiveHeading);

    return () => {
      window.removeEventListener("scroll", updateActiveHeading);
      window.removeEventListener("resize", updateActiveHeading);
    };
  }, [articleHeadings]);

  const activeHeadingLabel =
    articleHeadings.find((heading) => heading.id === activeHeading)?.text ||
    articleHeadings[0]?.text ||
    "Jump to section";

  if (slug && !currentPost) {
    return (
      <section className="mx-auto w-full max-w-4xl py-4">
        <h1 className="theme-text text-2xl font-semibold tracking-tight sm:text-3xl">
          {siteContent.blog.title}
        </h1>
        <p className="theme-text-muted mt-4 text-sm leading-7 sm:text-base">
          {siteContent.blog.missingPostMessage}
        </p>
        <Link to="/blog" className="theme-link mt-6 inline-flex text-sm font-medium">
          {siteContent.blog.backToPostsLabel}
        </Link>
      </section>
    );
  }

  if (!slug) {
    return (
      <section className="mx-auto w-full max-w-4xl py-4">
        <div className="max-w-3xl">
          <h1 className="theme-text text-2xl font-semibold tracking-tight sm:text-3xl">
            {siteContent.blog.title}
          </h1>
          <p className="theme-text-muted mt-3 text-sm leading-7 sm:text-base">
            {siteContent.blog.overview}
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {blogPosts.length ? (
            blogPosts.map((post) => (
              <article
                key={post.slug}
                className="theme-surface rounded-2xl border theme-border p-5 shadow-sm"
              >
                <div className="theme-text-subtle flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-medium uppercase tracking-[0.2em]">
                  {post.published ? (
                    <span>{formatDate(post.published)}</span>
                  ) : null}
                  {post.tags?.length
                    ? post.tags.map((tag) => <span key={tag}>{tag}</span>)
                    : null}
                </div>
                <h2 className="theme-text mt-3 text-xl font-semibold tracking-tight">
                  <Link to={`/blog/${post.slug}`} className="theme-link-plain">
                    {post.title}
                  </Link>
                </h2>
                <p className="theme-text-muted mt-3 max-w-3xl text-sm leading-7 sm:text-base">
                  {post.summary}
                </p>
                <Link to={`/blog/${post.slug}`} className="theme-link mt-4 inline-flex text-sm font-medium">
                  {siteContent.blog.readArticleLabel}
                </Link>
              </article>
            ))
          ) : (
            <div className="theme-surface theme-text-muted rounded-2xl border border-dashed theme-border p-6 text-sm">
              {siteContent.blog.emptyState}
            </div>
          )}
        </div>
      </section>
    );
  }

  const baseHeadingLevel = articleHeadings[0]?.level || 2;

  return (
    <section className="mx-auto w-full max-w-[1380px] py-4">
      <div className="blog-page-shell">
        <Link to="/blog" className="theme-link inline-flex text-sm font-medium">
          {siteContent.blog.backToPostsLabel}
        </Link>

        {articleHeadings.length ? (
          <div className="blog-toc-mobile xl:hidden">
            <button
              type="button"
              className="blog-toc-mobile-trigger"
              aria-expanded={isMobileTocOpen}
              onClick={() => setIsMobileTocOpen((value) => !value)}
            >
              <span>
                <span className="blog-toc-mobile-label">Sections</span>
                <span className="blog-toc-mobile-current">{activeHeadingLabel}</span>
              </span>
              <span className={`blog-toc-mobile-chevron${isMobileTocOpen ? " is-open" : ""}`}>
                ▾
              </span>
            </button>
          </div>
        ) : null}

        <div className="blog-article-layout mt-5">
          <div className="min-w-0">
            <header className="blog-article-header border-b theme-border pb-5">
              <div className="theme-text-subtle flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-medium uppercase tracking-[0.2em]">
                {currentPost?.published ? (
                  <span>{formatDate(currentPost.published)}</span>
                ) : null}
                {currentPost?.tags?.length
                  ? currentPost.tags.map((tag) => <span key={tag}>{tag}</span>)
                  : null}
              </div>
              <h1 className="theme-text mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {parsedArticle?.title || currentPost?.title}
              </h1>
              {currentPost?.summary ? (
                <p className="theme-text-muted mt-4 max-w-3xl text-sm leading-7 sm:text-base">
                  {currentPost.summary}
                </p>
              ) : null}
            </header>

            <article
              ref={articleRef}
              className="blog-content mt-8"
              dangerouslySetInnerHTML={{ __html: parsedArticle?.html || "" }}
            />
          </div>

          {articleHeadings.length ? (
            <aside className="blog-toc hidden xl:block">
              <div className="blog-toc-panel">
                <p className="blog-toc-title">On This Page</p>
                <nav aria-label="Table of contents" className="blog-toc-nav">
                  {articleHeadings.map((heading) => {
                    const isActive = heading.id === activeHeading;

                    return (
                      <a
                        key={heading.id}
                        href={`#${heading.id}`}
                        className={`blog-toc-link${isActive ? " is-active" : ""}`}
                        style={{
                          paddingLeft: `${(heading.level - baseHeadingLevel) * 0.9}rem`,
                        }}
                      >
                        {heading.text}
                      </a>
                    );
                  })}
                </nav>
              </div>
            </aside>
          ) : null}
        </div>
      </div>

      {articleHeadings.length && shouldRenderMobileToc ? (
        <div
          className={`blog-toc-mobile-overlay xl:hidden${isMobileTocVisible ? " is-open" : ""}`}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="blog-toc-mobile-backdrop"
            aria-label="Close sections"
            onClick={() => setIsMobileTocOpen(false)}
          />
          <div className="blog-toc-mobile-sheet">
            <div className="blog-toc-mobile-sheet-header">
              <div>
                <p className="blog-toc-title">Sections</p>
                <p className="blog-toc-mobile-sheet-current">{activeHeadingLabel}</p>
              </div>
              <button
                type="button"
                className="blog-toc-mobile-close"
                onClick={() => setIsMobileTocOpen(false)}
              >
                Close
              </button>
            </div>

            <nav aria-label="Table of contents" className="blog-toc-mobile-sheet-nav">
              {articleHeadings.map((heading) => {
                const isActive = heading.id === activeHeading;

                return (
                  <a
                    key={heading.id}
                    href={`#${heading.id}`}
                    className={`blog-toc-mobile-sheet-link${isActive ? " is-active" : ""}`}
                    style={{
                      paddingLeft: `${1 + (heading.level - baseHeadingLevel) * 1}rem`,
                    }}
                    onClick={() => setIsMobileTocOpen(false)}
                  >
                    {heading.text}
                  </a>
                );
              })}
            </nav>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default BlogPage;
